import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '../../lib/icons';
import { STATUS_COLOR, STATUS_LABEL, PRIORITY_LABEL, enumLabel } from '../../lib/format';
import type { MorningBriefDetail, MorningBriefItem, MorningBriefSection } from '@atlas/contracts';

/** Per-section icon + label + empty copy (kept in the app's icon idiom). */
export const SECTION_META: Record<MorningBriefSection, { icon: IconName; label: string; empty: string }> = {
  needs_attention: { icon: 'warning', label: 'Needs Attention', empty: 'No urgent items today.' },
  mention_comment: { icon: 'comment', label: 'Comment Mentions', empty: 'No new mentions since yesterday.' },
  mention_document: { icon: 'document', label: 'Document Mentions', empty: 'No document mentions.' },
  assigned_work: { icon: 'assigned', label: 'Assigned to You', empty: 'No open assigned work.' },
  watched_update: { icon: 'watch', label: 'Watched Updates', empty: 'No watched ticket updates.' },
  highlight: { icon: 'ai', label: 'Highlights', empty: 'No highlights yet.' },
  lowlight: { icon: 'blocked', label: 'Risks & Lowlights', empty: 'No risks flagged.' },
  recommended_focus: { icon: 'done', label: 'Recommended Focus', empty: 'Nothing specific to prioritize.' },
};

/** Display order for sections within the brief. */
export const SECTION_ORDER: MorningBriefSection[] = [
  'needs_attention',
  'recommended_focus',
  'mention_comment',
  'mention_document',
  'assigned_work',
  'watched_update',
  'highlight',
  'lowlight',
];

const SEVERITY_STYLE: Record<string, string> = {
  critical: 'bg-rose-100 text-rose-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
  info: 'bg-slate-100 text-slate-600',
};

const ITEM_ICON: Record<string, IconName> = {
  ticket: 'ticket',
  comment: 'comment',
  document: 'document',
  page: 'page',
  release: 'release',
  sprint: 'sprint',
  pr: 'swap',
  notification: 'bell',
  focus: 'done',
};

export function AiSourceBadge({ source }: { source: string | null | undefined }) {
  if (!source) return null;
  const llm = source === 'llm';
  return (
    <span
      className={`chip text-[10px] ${llm ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`}
      title={llm ? 'Generated by the configured AI provider' : 'Generated by the offline heuristic (no AI provider configured)'}
    >
      {llm ? <><Icon name="ai" /> AI</> : <><Icon name="dot-empty" /> heuristic</>}
    </span>
  );
}

export function SeverityChip({ severity }: { severity: string }) {
  if (severity === 'info') return null;
  return <span className={`chip text-[10px] ${SEVERITY_STYLE[severity] ?? SEVERITY_STYLE.info}`}>{severity}</span>;
}

/** A single brief item — card with a deep link to the entity + "View more" report. */
export function BriefItemCard({ item, onNavigate }: { item: MorningBriefItem; onNavigate?: () => void }) {
  const nav = useNavigate();
  const go = (url: string | null | undefined) => {
    if (!url) return;
    onNavigate?.();
    if (url.startsWith('http')) window.open(url, '_blank', 'noopener');
    else nav(url);
  };

  // Plain text rows (AI narrative lines / recommended focus) have no entity link.
  if (item.itemType === 'focus' && !item.sourceUrl) {
    return (
      <div className="flex items-start gap-2 text-sm py-1.5">
        <span className="opacity-60">{ITEM_ICON[item.itemType] ? <Icon name={ITEM_ICON[item.itemType]!} /> : '•'}</span>
        <span className="text-ink">{item.title}</span>
      </div>
    );
  }

  return (
    <div className="card p-3 hover:border-brand transition-colors">
      <div className="flex items-start gap-2">
        <span className="text-base leading-5">{ITEM_ICON[item.itemType] ? <Icon name={ITEM_ICON[item.itemType]!} /> : '•'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-ink truncate">{item.title}</span>
            <SeverityChip severity={item.severity} />
            {item.status && <span className={`chip text-[10px] ${STATUS_COLOR[item.status] ?? 'bg-surface2 text-muted'}`}>{STATUS_LABEL[item.status] ?? enumLabel(item.status)}</span>}
            {item.priority && <span className="chip text-[10px] bg-surface2 text-muted"><Icon name="chevron-up" /> {PRIORITY_LABEL[item.priority] ?? enumLabel(item.priority)}</span>}
          </div>
          {item.reason && <div className="text-xs text-muted mt-0.5">{item.reason}</div>}
          {item.summary && <div className="text-xs text-ink/80 mt-1 line-clamp-2">{item.summary}</div>}
          {item.suggestedAction && (
            <div className="text-xs text-accent mt-1"><Icon name="arrow-right" /> {item.suggestedAction}</div>
          )}
          <div className="flex items-center gap-3 mt-2">
            {item.sourceUrl && (
              <button className="text-xs text-brand hover:underline" onClick={() => go(item.sourceUrl)}>
                Open
              </button>
            )}
            {item.reportUrl && (
              <button className="text-xs text-muted hover:text-ink hover:underline" onClick={() => go(item.reportUrl)}>
                View in report
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** A section block: heading, "View more" link, and its item cards (or empty copy). */
export function BriefSectionBlock({
  section,
  items,
  onNavigate,
  compact,
}: {
  section: MorningBriefSection;
  items: MorningBriefItem[];
  onNavigate?: () => void;
  compact?: boolean;
}) {
  const nav = useNavigate();
  const meta = SECTION_META[section];
  const moreUrl = items[0]?.reportUrl ?? null;
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-1.5">
          <span><Icon name={meta.icon} /></span>
          {meta.label}
          {items.length > 0 && <span className="text-muted font-normal">· {items.length}</span>}
        </h4>
        {moreUrl && items.length > 0 && (
          <button
            className="text-xs text-brand hover:underline"
            onClick={() => {
              onNavigate?.();
              if (moreUrl.startsWith('http')) window.open(moreUrl, '_blank', 'noopener');
              else nav(moreUrl);
            }}
          >
            View more →
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted">{meta.empty}</p>
      ) : (
        <div className="space-y-2">
          {(compact ? items.slice(0, 4) : items).map((it) => (
            <BriefItemCard key={it.id} item={it} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </section>
  );
}

/** Greeting + date + AI summary + metric chips — the brief's header. */
export function BriefSummaryHeader({ brief }: { brief: MorningBriefDetail }) {
  const m = brief.metrics;
  const chips: { label: string; value: number; tone?: string }[] = [
    { label: 'Needs attention', value: m.important, tone: m.critical > 0 ? 'text-rose-600' : undefined },
    { label: 'Mentions', value: m.mentionComments + m.mentionDocuments },
    { label: 'Assigned', value: m.assignedTickets },
    { label: 'Watched', value: m.watchedUpdates },
    { label: 'Highlights', value: m.highlights },
    { label: 'Risks', value: m.lowlights },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-muted">
        <span>{formatBriefDate(brief.briefDate)}</span>
        {brief.windowStartAt && (
          <span title="Activity window">· since {new Date(brief.windowStartAt).toLocaleString()}</span>
        )}
        <AiSourceBadge source={brief.aiSource} />
      </div>
      {brief.summary && <p className="text-sm text-ink leading-relaxed">{brief.summary}</p>}
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <div key={c.label} className="rounded-lg bg-surface2 px-2.5 py-1.5 text-center min-w-[64px]">
            <div className={`text-lg font-semibold tabular-nums ${c.tone ?? 'text-ink'}`}>{c.value}</div>
            <div className="text-[10px] text-muted">{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function groupItems(items: MorningBriefItem[]): Map<MorningBriefSection, MorningBriefItem[]> {
  const map = new Map<MorningBriefSection, MorningBriefItem[]>();
  for (const it of items) {
    const arr = map.get(it.section) ?? [];
    arr.push(it);
    map.set(it.section, arr);
  }
  return map;
}

export function formatBriefDate(briefDate: string): string {
  // briefDate is YYYY-MM-DD (user-local). Render without TZ shifting.
  const [y, m, d] = briefDate.split('-').map(Number);
  if (!y || !m || !d) return briefDate;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}
